import { useState, useEffect, useRef, useCallback } from 'react'
import { Button, Modal } from 'antd'
import { useLocation, Outlet } from 'react-router-dom'
import { PlatformType } from '@/types/authorization'
import { useToast } from '@/contexts/ToastContext'
import { AdvicePrepAssistantHost } from '@/features/advisory-ai/assistant'
import SideNav from './SideNav'
import TopBar from './TopBar'

const SIDER_WIDTH = 268
const SIGNUP_WELCOME_STORAGE_KEY = 'di.signup.welcome'
const SIGNUP_CONNECTION_TOAST_STORAGE_KEY = 'di.signup.connectionToast'

export default function AppLayout() {
  const { addToast } = useToast()
  const [collapsed, setCollapsed] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [showSignupWelcome, setShowSignupWelcome] = useState(false)
  const [showBrWelcome, setShowBrWelcome] = useState(false)
  /**
   * A nav section a walkthrough has asked to be expanded. Tokenised, so
   * re-running a guide counts as a fresh request.
   */
  const [forcedNavKey, setForcedNavKey] = useState<{ key: string; token: number } | null>(null)
  const forcedNavToken = useRef(0)
  const location = useLocation()
  const isDashboardViewer = location.pathname.includes('/reports/dashboard-builder/view')

  /**
   * Bring the sidebar back for a walkthrough that points at it.
   *
   * The "Show me around" tour opens by pointing at where AdvicePrep lives in the
   * nav, which is invisible if you have collapsed the sidebar — so the assistant
   * asks for it back. Only AppLayout owns that state, hence the callback down
   * rather than the assistant reaching up.
   *
   * `navKey` names the item being pointed at. Revealing the sidebar is not
   * enough alone: SideNav only expands the section holding the current page, so
   * a step pointing at Integrations from an AdvicePrep route would find a link
   * inside a collapsed section with no box to spotlight.
   */
  const revealNav = useCallback((navKey?: string) => {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      setCollapsed(false)
    } else {
      setDrawerOpen(true)
    }
    if (navKey) setForcedNavKey({ key: navKey, token: forcedNavToken.current++ })
  }, [])

  const handleMenuClick = () => {
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches
    if (isDesktop) {
      setCollapsed((prev) => !prev)
      return
    }

    setDrawerOpen(true)
  }

  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (location.pathname.startsWith('/reports/dashboard-builder')
        && window.matchMedia('(min-width: 1024px)').matches) {
      setCollapsed(true)
    }
  }, [location.pathname])

  useEffect(() => {
    const raw = sessionStorage.getItem(SIGNUP_WELCOME_STORAGE_KEY)
    if (!raw) return

    try {
      const parsed = JSON.parse(raw) as { product?: number | null; isBrSignup?: boolean }
      setShowBrWelcome(Boolean(parsed.isBrSignup || parsed.product === PlatformType.BRWFM))
      setShowSignupWelcome(true)
    } catch {
      sessionStorage.removeItem(SIGNUP_WELCOME_STORAGE_KEY)
    }
  }, [])

  useEffect(() => {
    const raw = sessionStorage.getItem(SIGNUP_CONNECTION_TOAST_STORAGE_KEY)
    if (!raw) return

    try {
      const parsed = JSON.parse(raw) as { message?: string }
      if (parsed.message) addToast(parsed.message, 'success')
    } finally {
      sessionStorage.removeItem(SIGNUP_CONNECTION_TOAST_STORAGE_KEY)
    }
  }, [addToast])

  const handleCloseSignupWelcome = () => {
    sessionStorage.removeItem(SIGNUP_WELCOME_STORAGE_KEY)
    setShowSignupWelcome(false)
  }

  return (
    <div className="min-h-screen flex bg-gray-50/90">
      <aside
        className="hidden lg:block fixed left-0 top-0 bottom-0 z-40 overflow-y-auto transition-[width] duration-200 ease-out"
        style={{ width: collapsed ? 0 : SIDER_WIDTH }}
      >
        <SideNav
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          forceOpenItem={forcedNavKey}
        />
      </aside>
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/20 lg:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden
        />
      )}
      <aside
        className={`fixed left-0 top-0 bottom-0 z-50 w-[268px] bg-white border-r border-gray-200 shadow-xl transition-transform duration-200 ease-out lg:hidden ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <SideNav collapsed={false} onToggle={() => setDrawerOpen(false)} forceOpenItem={forcedNavKey} />
      </aside>
      <div className={`flex-1 min-w-0 flex flex-col min-h-screen ${!collapsed ? 'lg:ml-[268px]' : ''}`}>
        <TopBar sidebarCollapsed={collapsed} onMenuClick={handleMenuClick} />
        <main className={`flex-1 p-6 overflow-x-hidden ${isDashboardViewer ? 'bg-[#eef1f6]' : ''}`}>
          {/* Preppy rides above the outlet so one instance serves every
              AdvicePrep route. It renders children untouched everywhere else. */}
          <AdvicePrepAssistantHost onRevealNav={revealNav}>
            <Outlet />
          </AdvicePrepAssistantHost>
        </main>
      </div>
      <Modal
        open={showSignupWelcome}
        title="Welcome"
        centered
        closable={false}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseSignupWelcome}>
            Close
          </Button>,
        ]}
      >
        <div className="space-y-4 text-center text-sm text-slate-700">
          <p>Welcome to Dashboard Insights, Use the menu on the left to navigate through your company dashboards</p>
          {showBrWelcome && (
            <p>
              To get the most from your WFM Dashboards, access your Getting Started Guide{' '}
              <a
                href="https://dashboardinsights.com/blog/wfm-dashboards-getting-started-guide/"
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-sky-700"
              >
                here
              </a>
            </p>
          )}
        </div>
      </Modal>
    </div>
  )
}
